import { AI_GRADIENT_SMALL_IMAGE_URL } from "@/components/common/ai-assets/ai-gradients";
import { useAnnotationPermissions } from "@/hooks/use-annotation-permissions";
import { WalkModeRenderOrders } from "@/modes/walk-mode/walk-mode-render-orders";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { useAppSelector } from "@/store/store-hooks";
import { usePanoCameraTransform } from "@/utils/camera-transform";
import { useSvg } from "@faro-lotv/app-component-toolbox";
import type { GUID } from "@faro-lotv/foundation";
import type { IElementImg360 } from "@faro-lotv/ielement-types";
import type { CachedWorldTransform } from "@faro-lotv/project-source";
import {
  useApiClientContext,
  type AiCocoRleMask,
  type AiMaskFile,
  type BoundingBox,
} from "@faro-lotv/service-wires";
import { useMemo, useRef, type JSX } from "react";
import type { Mesh } from "three";
import { aiFindingColor, isFindingCategory } from "./ai-finding-color";
import { maskBoxCoordinates, useAiMasks } from "./ai-mask-file";
import {
  selectBoundingBoxesOfElement,
  selectFocusedBoundingBoxId,
  selectShowBoundingBoxes,
} from "./ai-selectors";
import {
  boundingBoxCenterWorld,
  boundingBoxTopLeftLocal,
} from "./bounding-box-geometry";
import { BoundingBoxLabel } from "./bounding-box-label";
import { BoundingBoxMesh } from "./bounding-box-mesh";
import {
  BoundingMaskMesh,
  type BoundingMaskMeshProps,
} from "./bounding-mask-mesh";
import {
  boundingRegionsTopLeftLocal,
  isDrawableBoundingRegion,
} from "./bounding-region-geometry";
import { BoundingRegionMesh } from "./bounding-region-mesh";
import { useBoundingBoxAnnotation } from "./use-bounding-box-annotation";

type BoundingBoxRendererProps = {
  /** The panorama image element to render the bounding box on */
  pano: IElementImg360;
};

/**
 * @returns a semi-transparent bounding box overlay on a spherical panorama.
 * The bounding box is rendered as a mesh on the sphere surface using spherical coordinates.
 * With polygons enabled, a finding is drawn as its mask if it has one, else as its regions, else as its box.
 * A mask shows the finding's label while hovered, and opens the annotation dialog when clicked.
 */
export function BoundingBoxRenderer({
  pano,
}: BoundingBoxRendererProps): JSX.Element | null {
  // Get the camera transformation for positioning the mesh in 3D space
  const transform = usePanoCameraTransform(pano);

  const boundingBoxes = useAppSelector(selectBoundingBoxesOfElement(pano.id));

  // When the "show all" toggle is off, only the box the user focused (e.g. by
  // clicking a finding in the chat) is rendered. When the toggle is on, all
  // boxes for this element are shown.
  const showAll = useAppSelector(selectShowBoundingBoxes);
  const focusedBoundingBoxId = useAppSelector(selectFocusedBoundingBoxId);

  const visibleBoxes = useMemo(
    () =>
      boundingBoxes.filter(
        (box): box is BoundingBox =>
          !!box && (showAll || box.boundingBoxId === focusedBoundingBoxId),
      ),
    [boundingBoxes, showAll, focusedBoundingBoxId],
  );

  const arePolygonsEnabled = useAppSelector(
    selectHasFeature(Features.AiFindingsPolygon),
  );

  // The masks of an element are only loaded when one of the shown findings has one.
  const { projectApiClient } = useApiClientContext();
  const { masks, imageSize, isLoading } = useAiMasks(
    projectApiClient,
    arePolygonsEnabled && visibleBoxes.some((box) => box.hasMask)
      ? pano.id
      : undefined,
  );
  const findingMasks = useMemo(
    () => findingMasksOf(masks, imageSize, boundingBoxes),
    [masks, imageSize, boundingBoxes],
  );

  if (!visibleBoxes.length || isLoading) return null;

  return (
    <>
      {visibleBoxes.map((box) => (
        <BoxRenderer
          key={box.boundingBoxId}
          box={box}
          transform={transform}
          mask={findingMasks.get(box.boundingBoxId)}
        />
      ))}
    </>
  );
}

/** Distinct colors for the masks of findings without a known type */
const MASK_COLORS = [
  "#e6194b",
  "#3cb44b",
  "#4363d8",
  "#f58231",
  "#911eb4",
  "#42d4f4",
  "#f032e6",
  "#bfef45",
  "#ffe119",
  "#469990",
];

/** The mask of a finding, and how to draw it */
type FindingMask = Pick<
  BoundingMaskMeshProps,
  "mask" | "imageSize" | "color" | "renderOrder"
>;

/**
 * @param masks The masks of a panorama, by bounding box ID.
 * @param imageSize The size of the image the masks were computed on.
 * @param boxes The boxes of the panorama, for the types of their findings.
 * @returns How to draw each mask: the color of its finding's type, or else one of the palette, and render orders
 * between the AI boxes and their outlines. Both follow the order of the box IDs, so they are stable whichever
 * findings are shown.
 */
function findingMasksOf(
  masks: Map<string, AiCocoRleMask>,
  imageSize: AiMaskFile["imageSize"] | undefined,
  boxes: Array<BoundingBox | undefined>,
): Map<string, FindingMask> {
  if (!imageSize) return new Map();
  const types = new Map<GUID, string | undefined>(
    boxes.flatMap((box) => (box ? [[box.boundingBoxId, box.type]] : [])),
  );
  const sorted = [...masks].sort(([a], [b]) => (a < b ? -1 : 1));
  const step =
    (WalkModeRenderOrders.AiBoundingBoxOutlines -
      WalkModeRenderOrders.AiBoundingBoxes) /
    (sorted.length + 1);
  let untyped = 0;
  return new Map(
    sorted.map(([boxId, mask], i) => {
      const type = types.get(boxId);
      const color = isFindingCategory(type)
        ? aiFindingColor(type)
        : MASK_COLORS[untyped++ % MASK_COLORS.length];
      return [
        boxId,
        {
          mask,
          imageSize,
          color,
          renderOrder: WalkModeRenderOrders.AiBoundingBoxes + (i + 1) * step,
        },
      ];
    }),
  );
}

type BoxRendererProps = {
  box: BoundingBox;
  transform: CachedWorldTransform;
  /** The finding's mask, drawn instead of its regions or box and their label */
  mask?: FindingMask;
};

function BoxRenderer({
  box,
  transform,
  mask,
}: BoxRendererProps): JSX.Element | null {
  const { boundingBox, boundingRegions, type, label, title, elementId } = box;

  // Reference to the rendered mesh, used to capture a screenshot of the box.
  const meshRef = useRef<Mesh>(null);

  const gradient = useSvg(AI_GRADIENT_SMALL_IMAGE_URL);

  const arePolygonsEnabled = useAppSelector(
    selectHasFeature(Features.AiFindingsPolygon),
  );

  // The polygons follow the shape of the finding, the box is the coarse fallback
  // for the findings the backend did not segment.
  const regions = useMemo(() => {
    const drawable = arePolygonsEnabled
      ? boundingRegions?.filter(isDrawableBoundingRegion)
      : undefined;
    return drawable?.length ? drawable : undefined;
  }, [arePolygonsEnabled, boundingRegions]);

  const color = aiFindingColor(type);

  // The part of the image the mask covers, which is usually tighter than the box.
  const maskBox = useMemo(
    () => mask && maskBoxCoordinates(mask.mask, mask.imageSize),
    [mask],
  );

  // Top-left corner of the finding on the unit sphere, used as the label anchor.
  const topLeftLocal = useMemo(
    () =>
      regions
        ? boundingRegionsTopLeftLocal(regions)
        : boundingBoxTopLeftLocal(boundingBox),
    [regions, boundingBox],
  );

  // World-space position of the finding's center, used as the annotation target.
  const centerWorld = useMemo(
    () => boundingBoxCenterWorld(maskBox ?? boundingBox, transform),
    [maskBox, boundingBox, transform],
  );

  const { canWriteAnnotations } = useAnnotationPermissions();

  const { createBboxAnnotation } = useBoundingBoxAnnotation({
    centerWorld,
    elementId,
    label,
    title,
    meshRef,
  });

  const onClick = canWriteAnnotations ? createBboxAnnotation : undefined;

  // Without its box, a finding shows its label while the mask is hovered.
  if (mask) {
    return (
      <group position={transform.position} quaternion={transform.quaternion}>
        <BoundingMaskMesh
          {...mask}
          onClick={onClick}
          tooltip={label}
          meshRef={meshRef}
        />
      </group>
    );
  }

  // The polygons follow the shape of the finding, the box is the coarse fallback
  // for the findings the backend did not segment.
  let findingMesh: JSX.Element | null = null;
  if (regions) {
    findingMesh = (
      <BoundingRegionMesh regions={regions} color={color} meshRef={meshRef} />
    );
  } else if (gradient) {
    findingMesh = (
      <BoundingBoxMesh
        boundingBox={boundingBox}
        gradient={gradient}
        meshRef={meshRef}
      />
    );
  }

  // The label anchors to the mesh and captures a screenshot of it, so the whole finding waits for
  // the gradient rather than showing a label without its box.
  if (!findingMesh) return null;

  return (
    <group position={transform.position} quaternion={transform.quaternion}>
      {findingMesh}

      <BoundingBoxLabel
        position={topLeftLocal}
        label={label}
        color={regions ? color : undefined}
        onClick={onClick}
        allowAnnotationCreation={canWriteAnnotations}
      />
    </group>
  );
}
